// The assistant: conversations on the left with what is waiting for you, the conversation on the right.
// Answers can carry a facts table, numbered sources and follow-up questions; anything it wants to do comes as a
// card you approve (here, by saying "approve", in Requests or from the notification). New conversation: starters
// made from your own work. Speak into the composer (the waveform follows your voice), attach with the clip or by
// dropping files anywhere on the conversation. /assistant/waiting lists every card waiting for you.
// The thread and the composer are the same ones the assistant panel uses (src/assistant).
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { isSuggestion, type ProposedAction } from '@dpw/shared';
import { api } from '../api';
import { useActions, useChange, useChecksToAnswer, useConversations, useSpaces, useStarters } from '../lib/queries';
import { useLinger } from '../lib/linger';
import { listItem, spring, stagger } from '../lib/motion';
import { addDays, ago, localDay, localTime, agoStart, localDate } from '../lib/format';
import { ApproveCard } from '../ui/ApproveCard';
import { Count, Orb, Skel, SkelList, type OrbState } from '../ui/motion';
import { IconClip, IconPlus } from '../ui/icons';
import { useToast } from '../ui/toast';
import { usePhone } from '../lib/media';
import { P } from '../phone/kit';
import { useWaitingCount } from '../phone/PhoneShell';
import { PhoneHistory, PhoneStart, ScopeSheet } from '../phone/Assistant';
import { useChat } from '../assistant/useChat';
import { Thread } from '../assistant/Thread';
import { Composer } from '../assistant/Composer';
import { useVoiceMode } from '../voice/useVoiceMode';
import { VoiceBar, VoiceModeButton } from '../assistant/VoiceBar';

export function AssistantPage({ waitingView = false }: { waitingView?: boolean }) {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const spaces = useSpaces();
  const [text, setText] = useState('');
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const setSpace = useChange((s: string | null) => api.assistant.setSpace(id!, s));
  const phone = usePhone();
  const waitingN = useWaitingCount();
  const [view, setView] = useState<'chat' | 'hist'>('chat');
  const [scopeOpen, setScopeOpen] = useState(false);
  // A new conversation's scope, chosen before the first message (the phone's scope button).
  const [newScope, setNewScope] = useState<string | null>(null);
  // The space a new conversation looks at, when it was started from a space page (?space=).
  const lookAt = useRef<string | null>(null);
  // A new conversation is created and opened first, so the question never has to move from one screen to another.
  const chat = useChat(id, {
    key: 'page',
    create: () => { const s = lookAt.current; lookAt.current = null; return api.assistant.create({ spaceId: s }); },
    onCreated: cid => nav(`/assistant/${cid}`),
  });
  const { conv, messages, thinking, busy } = chat;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [messages.length, thinking]);
  // A message that could not be sent goes back in the box.
  useEffect(() => { if (chat.failedText) setText(chat.failedText); }, [chat.failedText]);

  const say = useCallback((t: string) => {
    if (!t.trim() || busy) return;
    setText('');
    void chat.say(t);
  }, [busy, chat]);
  // Voice mode, in this same chat (Talk it through is no longer a page of its own).
  const voice = useVoiceMode((t, o) => chat.say(t, o));
  const wantsVoice = params.get('voice') === '1';

  // From the command palette: /assistant?ask=...
  const asked = useRef(false);
  useEffect(() => {
    const a = params.get('ask'), fill = params.get('say');
    if ((a || fill) && !id && !asked.current) {
      asked.current = true;
      lookAt.current = params.get('space');
      setParams({}, { replace: true });
      // ask: send it now. say: put it in the box to finish ("Add to the AI Office context that ").
      if (a) say(a);
      else if (fill) { setText(fill); window.setTimeout(() => { input.current?.focus(); input.current?.setSelectionRange(fill.length, fill.length); }, 0); }
    }
  }, [params, id, say, setParams]);

  // Files: the clip, or dropped anywhere on the conversation. A new conversation is created first.
  const attachFiles = async (files: File[]) => {
    try { await chat.attach(files); input.current?.focus(); } catch (e) { toast(e instanceof Error ? e.message : 'Could not attach that.'); }
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); void attachFiles([...e.dataTransfer.files]); };

  const orbState: OrbState = thinking ? 'thinking' : 'idle';
  const waitingHere = chat.actions.some(a => a.status === 'waiting');
  const placeholder = waitingHere ? 'Say approve, ignore, or what to change' : id ? 'Reply, or tell it to do something' : 'Ask anything, or tell it to remind, create, draft or research';
  const showEmpty = !id && !busy && !waitingView && !voice.on;
  const composer = (ph: boolean) => voice.on ? (
    <div className={ph ? 'ph-foot' : 'compose'}><VoiceBar voice={voice} phone={ph} /></div>
  ) : (
    <Composer extra={<VoiceModeButton voice={voice} phone={ph} disabled={thinking} />} value={text} onChange={setText} onSend={say} onFiles={f => void attachFiles(f)} staged={chat.staged} thinking={thinking}
      placeholder={ph ? (waitingHere ? 'Say approve, or what to change' : 'Ask anything') : placeholder} phone={ph} inputRef={input}
      stagedNote={<span className="xs mut">Say where it should go, or I will suggest a place.</span>} />
  );

  if (phone) {
    const scopeId = id ? conv.data?.spaceId ?? null : newScope;
    const scopeName = scopeId ? (spaces.data ?? []).find(x => x.id === scopeId)?.name ?? 'A space' : 'Everything';
    const pickScope = (sp: string | null) => { if (id) setSpace.mutate(sp); else { setNewScope(sp); lookAt.current = sp; } };
    return (
      <div className="ph-frame ph-ask">
        <div className="ph-tbar">
          <Link className="ph-nb" to="/" aria-label="Close"><P.x /></Link>
          <button type="button" className="ph-nb" aria-label="All conversations" onClick={() => setView('hist')}><P.hist /></button>
          <span className="sp" />
          <button type="button" className="ph-scopeb" aria-label={`Looking at ${scopeName}. Change`} onClick={() => setScopeOpen(true)}><span>{scopeName}</span><P.chev /></button>
          <button type="button" className="ph-nb" aria-label="New conversation" onClick={() => { setView('chat'); setNewScope(null); nav('/assistant'); }}><P.compose /></button>
        </div>
        {waitingN > 0 && !waitingView && view === 'chat' && (
          <Link className="ph-wbar" to="/assistant/waiting"><span className="aiorb" /><b>Waiting for you</b><span className="n">{waitingN}</span><P.chev /></Link>
        )}
        <div className="ph-body">
          {view === 'hist' ? <PhoneHistory onBack={() => setView('chat')} />
            : waitingView ? <>
              <div className="ph-vh"><button type="button" className="ph-back" onClick={() => nav(-1)}><P.back /><span>Back</span></button></div>
              <div className="ph-lt" style={{ padding: '0 2px 14px' }}><h1 className="md">Waiting for you</h1><div className="sub">Everything the assistant wants to do. Nothing happens until you approve.</div></div>
              <WaitingView bare />
            </>
            : showEmpty ? <PhoneStart orb={orbState} onHistory={() => setView('hist')} onVoice={voice.start} />
            : (
              <div className="ph-thread">
                <Thread messages={messages} actions={chat.actions} thinking={thinking} reply={chat.liveReply} live={voice.on ? voice.heard : null} loading={!!id && conv.isLoading} error={chat.error} onAsk={say} phone />
                <div ref={end} />
              </div>
            )}
        </div>
        {!waitingView && view === 'chat' && composer(true)}
        <AnimatePresence>{scopeOpen && <ScopeSheet key="scope" value={scopeId} onPick={pickScope} onClose={() => setScopeOpen(false)} />}</AnimatePresence>
      </div>
    );
  }

  return (
    <div className="asw">
      <Rail activeId={id} waitingView={waitingView} />
      <section className={`pane${drag ? ' drop' : ''}`} aria-label="Conversation"
        onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDrag(true); } }}
        onDragLeave={e => { if (e.currentTarget === e.target) setDrag(false); }} onDrop={onDrop}>
        <div className="ptb">
          <span className="tt">{waitingView ? 'Waiting for you' : conv.data?.title ?? 'New conversation'}</span>
          {id && conv.data && (
            <label className="scp">Looking at
              <select value={conv.data.spaceId ?? ''} onChange={e => setSpace.mutate(e.target.value || null)}>
                <option value="">Everything</option>
                {(spaces.data ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="scroll">
          {waitingView ? <WaitingView /> : (
            <div className="thread">
              {showEmpty && <Empty orb={orbState} onVoice={voice.start} voiceFirst={wantsVoice} onPick={(t, complete) => { if (complete) say(t); else { setText(t); window.setTimeout(() => { input.current?.focus(); input.current?.setSelectionRange(t.length, t.length); }, 0); } }} />}
              <Thread messages={messages} actions={chat.actions} thinking={thinking} reply={chat.liveReply} live={voice.on ? voice.heard : null} loading={!!id && conv.isLoading} error={chat.error} onAsk={say} />
              <div ref={end} />
            </div>
          )}
        </div>
        {!waitingView && composer(false)}
        <AnimatePresence>
          {drag && (
            <motion.div className="dropov" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div className="dropin" initial={{ scale: 0.96 }} animate={{ scale: 1, transition: spring.gentle }}><IconClip /><b>Drop to attach</b><span className="xs mut">Then say where it goes.</span></motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}

function Empty({ orb, onPick, onVoice, voiceFirst }: { orb: OrbState; onPick: (t: string, complete: boolean) => void; onVoice: () => void; voiceFirst: boolean }) {
  const starters = useStarters();
  return (
    <div className="aempty">
      <span className="bigorbwrap"><Orb size={56} state={orb} /></span>
      <h2>What do you need?</h2>
      <p>Ask anything, or tell it what to do: remind, create, file, draft, research. It always shows you a card first, and nothing happens until you approve.</p>
      <button type="button" className={`bt${voiceFirst ? ' pri' : ''}`} onClick={onVoice}>Talk it through</button>
      <div className="trys">
        <span className="k">Try saying</span>
        {starters.isLoading ? <div className="stl">{[0, 1, 2, 3].map(i => <div className="stc" key={i}><Skel w={`${60 + i * 8}%`} h={13} /></div>)}</div> : (
          <motion.div className="stl" variants={{ animate: { transition: stagger(0.05, 0.05) } }} initial="initial" animate="animate">
            {(starters.data ?? []).map(s => (
              <motion.button key={s.text} type="button" className="stc" variants={listItem} whileTap={{ scale: 0.99 }} onClick={() => onPick(s.text, s.complete)}>
                <span>{s.text}{!s.complete && <span className="mut">...</span>}</span><span className="xs mut">{s.hint}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}


/** Conversations, and everything waiting for you: cards to approve, questions to answer, context suggestions (uncounted). */
function Rail({ activeId, waitingView }: { activeId?: string | undefined; waitingView: boolean }) {
  const nav = useNavigate();
  const convs = useConversations();
  const waiting = useActions('waiting');
  const checks = useChecksToAnswer();
  const list = convs.data ?? [];
  const today = localDate();
  const weekAgo = addDays(today, -6);
  const toApprove = (waiting.data ?? []).filter(a => !isSuggestion(a));
  const ctx = useMemo(() => {
    const m = new Map<string, { name: string; n: number }>();
    for (const a of waiting.data ?? []) if (isSuggestion(a)) { const k = a.spaceId ?? ''; m.set(k, { name: a.spaceName ?? 'No space', n: (m.get(k)?.n ?? 0) + 1 }); }
    return [...m.values()];
  }, [waiting.data]);
  const questions = (checks.data ?? []).filter(c => c.view === 'full' && c.status === 'open');
  const groups: [string, typeof list][] = [
    ['Today', list.filter(c => localDay(c.updatedAt) === today)],
    ['This week', list.filter(c => localDay(c.updatedAt) !== today && localDay(c.updatedAt) >= weekAgo)],
    ['Earlier', list.filter(c => localDay(c.updatedAt) < weekAgo)],
  ];
  return (
    <aside className="rail" aria-label="Conversations">
      <motion.button whileTap={{ scale: 0.98 }} className="newc" onClick={() => nav('/assistant')}><IconPlus />New conversation</motion.button>
      <div className="rsec">Waiting for you <span className="n"><Count value={toApprove.length + questions.length} /></span></div>
      <LayoutGroup id="rail">
        {toApprove.length > 0 && (
          <motion.button layout className={`cv q${waitingView ? ' on' : ''}`} onClick={() => nav('/assistant/waiting')}>
            <span className="qt"><Orb size={8} />To approve</span>
            <span className="t"><Count value={toApprove.length} /> {toApprove.length === 1 ? 'action' : 'actions'} waiting for your approval</span>
          </motion.button>
        )}
        {questions.map(c => c.view === 'full' && (
          <motion.button layout key={c.id} className="cv q" onClick={() => nav(`/checks/${c.id}`)}>
            <span className="qt"><Orb size={8} />{c.about?.title ?? 'Question'}</span><span className="t">{c.question}</span>
          </motion.button>
        ))}
        {ctx.map(g => (
          <motion.button layout key={g.name} className="cv q opt" onClick={() => nav('/requests')}>
            <span className="qt"><Orb size={8} />{g.name}</span><span className="t">Context suggestions: {g.n}</span>
          </motion.button>
        ))}
        {!toApprove.length && !questions.length && !ctx.length && !waiting.isLoading && <div className="xs mut" style={{ padding: '2px 10px 6px' }}>Nothing waiting.</div>}
      </LayoutGroup>
      {convs.isLoading ? <SkelList rows={4} variant="conv" /> : groups.map(([label, cs]) => cs.length > 0 && (
        <div key={label} className="rgrp">
          <div className="rsec">{label}</div>
          {cs.map(c => (
            <button key={c.id} className={`cv${c.id === activeId ? ' on' : ''}`} onClick={() => nav(`/assistant/${c.id}`)} aria-current={c.id === activeId ? 'page' : undefined}>
              {c.id === activeId && <motion.span layoutId="cv-on" className="cvon" transition={spring.snappy} />}
              <span className="t">{c.title}</span>
              <span className="m">{[c.spaceName, localDay(c.updatedAt) === today ? localTime(c.updatedAt) : c.spaceName ? ago(c.updatedAt) : agoStart(c.updatedAt)].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      ))}
      <div className="rfoot">It sees every space, track, activity and update you can see. It always asks before it creates, sends or changes anything.</div>
    </aside>
  );
}

const LINK_FOR: Partial<Record<ProposedAction['source'], string>> = {
  email: 'See the email in Requests', logged_email: 'See the email in Requests', recording: 'Review each action in Requests', minutes: 'Review each action in Requests',
  notetaker: 'See the minutes in Requests', invite: 'See the invite in Requests', gone_quiet: 'See it in Requests',
};

function WaitingView({ bare = false }: { bare?: boolean }) {
  const waiting = useActions('waiting');
  const all = useMemo(() => waiting.data ?? [], [waiting.data]);
  const cards = useLinger(all.filter(a => !isSuggestion(a)), a => a.status !== 'waiting');
  const optional = all.filter(a => isSuggestion(a) && a.status === 'waiting').length;
  return (
    <div className="thread">
      {!bare && (
        <div className="whead">
          <h2>Waiting for your approval</h2>
          <p className="mut">Everything the assistant wants to do and has not done yet: from your chats, forwarded emails, meeting minutes and check-ins. Approve here, in Requests, or by saying approve.</p>
        </div>
      )}
      {waiting.isLoading ? <SkelList rows={3} /> : (
        <AnimatePresence mode="popLayout" initial={false}>
          {cards.map(a => (
            <motion.div key={a.id} layout variants={listItem} initial="initial" animate="animate" exit="exit" className="wcard">
              <ApproveCard action={a} />
              {a.conversationId
                ? <Link className="xs ulink" to={`/assistant/${a.conversationId}`}>Open the conversation</Link>
                : <Link className="xs ulink" to={`/requests?id=${a.id}`}>{LINK_FOR[a.source] ?? 'See it in Requests'}</Link>}
            </motion.div>
          ))}
          {!cards.length && <motion.div key="none" className="empty0" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><b>Nothing is waiting for your approval.</b></motion.div>}
          {optional > 0 && <motion.div key="opt" layout className="xs mut"><Link className="ulink" to="/requests">{optional} context {optional === 1 ? 'suggestion' : 'suggestions'} in Requests</Link>. Optional, so not counted here.</motion.div>}
        </AnimatePresence>
      )}
    </div>
  );
}

